import React from 'react';
import { motion } from 'framer-motion';
import { PartyPopper, Baby, Heart, Briefcase, GraduationCap, Gift, Sparkles, Users, Clock, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
const eventTypes = [{
  icon: Baby,
  title: 'Baby Showers',
  description: 'Welcoming new life with joy and celebration.'
}, {
  icon: Heart,
  title: 'Anniversaries',
  description: 'Honoring love stories and milestones together.'
}, {
  icon: Briefcase,
  title: 'Corporate Events',
  description: 'Professional gatherings that leave lasting impressions.'
}, {
  icon: GraduationCap,
  title: 'Graduation Parties',
  description: 'Celebrating academic achievements in style.'
}, {
  icon: Gift,
  title: 'Bridal Showers',
  description: 'Pre-wedding celebrations for the bride-to-be.'
}, {
  icon: Sparkles,
  title: 'Holiday Celebrations',
  description: 'Festive events for Christmas, New Year, and more.'
}];
const features = [{
  icon: Users,
  title: 'Personalized Service',
  description: 'Every event is unique. We tailor our approach to match your specific needs and vision.'
}, {
  icon: Sparkles,
  title: 'Creative Excellence',
  description: 'Our team brings fresh ideas and creative solutions to make your event stand out.'
}, {
  icon: Clock,
  title: 'Stress-Free Planning',
  description: 'We handle all the details so you can focus on enjoying the celebration.'
}];
export function SpecialEventsPage() {
  return <div className="min-h-screen" data-id="element-253">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-purple-50 via-white to-purple-100 pt-24" data-id="element-254">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-255">
          <div className="absolute -top-[20%] -right-[10%] w-[500px] h-[500px] rounded-full bg-purple-200/40 blur-3xl" data-id="element-256" />
          <div className="absolute bottom-[10%] -left-[10%] w-[400px] h-[400px] rounded-full bg-purple-100/50 blur-3xl" data-id="element-257" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center py-16" data-id="element-258">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-259">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-260">
              <PartyPopper className="w-10 h-10 text-purple-500" data-id="element-261" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-stone-900 mb-6" data-id="element-262">
              Special Events
            </h1>
            <p className="text-xl md:text-2xl text-stone-600 max-w-2xl mx-auto" data-id="element-263">
              Every occasion deserves to be celebrated
            </p>
          </motion.div>
        </div>
      </section>

      {/* Event Types */}
      <section className="py-20 bg-white" data-id="element-264">
        <div className="container mx-auto px-4" data-id="element-265">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-266">
            <h2 className="text-4xl font-bold text-stone-900 mb-4" data-id="element-267">
              Events We Plan
            </h2>
            <div className="w-24 h-1 bg-purple-500 mx-auto rounded-full" data-id="element-268" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-269">
            {eventTypes.map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-stone-50 rounded-2xl p-8 border border-stone-100" data-id="element-270">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6" data-id="element-271">
                  <item.icon className="w-7 h-7 text-purple-500" data-id="element-272" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-3" data-id="element-273">
                  {item.title}
                </h3>
                <p className="text-stone-600" data-id="element-274">{item.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-stone-50" data-id="element-275">
        <div className="container mx-auto px-4" data-id="element-276">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-277">
            <h2 className="text-4xl font-bold text-stone-900 mb-4" data-id="element-278">
              Why Choose Us
            </h2>
            <div className="w-24 h-1 bg-purple-500 mx-auto rounded-full" data-id="element-279" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-280">
            {features.map((item, index) => <motion.div key={item.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.15
          }} className="bg-white rounded-2xl p-8 border border-stone-100 text-center" data-id="element-281">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-282">
                  <item.icon className="w-8 h-8 text-purple-500" data-id="element-283" />
                </div>
                <h3 className="text-xl font-bold text-stone-900 mb-3" data-id="element-284">
                  {item.title}
                </h3>
                <p className="text-stone-600" data-id="element-285">{item.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-purple-500 text-white" data-id="element-286">
        <div className="container mx-auto px-4 text-center" data-id="element-287">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} data-id="element-288">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-289">
              Let's plan something special
            </h2>
            <p className="text-xl text-purple-100 mb-10 max-w-2xl mx-auto" data-id="element-290">
              Whatever the occasion, we're here to help you celebrate in style.
            </p>
            <a href="https://wa.me/260968642042" target="_blank" rel="noopener noreferrer" data-id="element-291">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-purple-50 shadow-lg gap-2" data-id="element-292">
                <MessageCircle className="w-5 h-5" data-id="element-293" />
                Start Planning on WhatsApp
              </Button>
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
}